/**
 * Shared cells of the clients list (KAN-616): used by both the desktop table
 * and the mobile card so the rendering rules live in one place.
 */
import { Loader2, MessageSquare } from 'lucide-react';
import { StageBadge } from '@/components/funnel/FunnelBadges';
import type { Client } from '@/types/client';

interface CellProps {
  client: Client;
  /** data-testid of the rendered element (differs for table / mobile card). */
  testId: string;
}

interface MessagesCountProps extends CellProps {
  /** Opens the client's transcript; without it the count is plain text. */
  onOpenTranscript?: (client: Client) => void;
  /** data-testid of the transcript button. */
  transcriptTestId?: string;
  /** Estimates are being loaded for this client. */
  loading?: boolean;
}

export function ClientMessagesCount({
  client,
  testId,
  onOpenTranscript,
  transcriptTestId,
  loading = false,
}: MessagesCountProps): JSX.Element {
  const count = client.messages_count ?? 0;
  const label = <span data-testid={testId}>{count}</span>;
  if (count === 0 || !onOpenTranscript) return label;
  return (
    <button
      type="button"
      data-testid={transcriptTestId}
      aria-label="Открыть переписку"
      title="Открыть переписку"
      aria-busy={loading}
      disabled={loading}
      className="inline-flex items-center gap-1 cursor-pointer font-medium text-blue-600 hover:text-blue-800 hover:underline focus-visible:underline focus-visible:outline-none disabled:opacity-60 disabled:cursor-wait"
      onClick={(e) => {
        e.stopPropagation();
        onOpenTranscript(client);
      }}
    >
      <MessageSquare className="h-3.5 w-3.5" aria-hidden="true" />
      {label}
      {loading && <Loader2 className="h-3 w-3 animate-spin" aria-hidden="true" />}
    </button>
  );
}

export function ClientFunnelStage({ client, testId }: CellProps): JSX.Element {
  const stages = (client.funnel_positions ?? []).flatMap((p) =>
    p.stage ? [{ key: String(p.funnel.id), stage: p.stage }] : []
  );
  return (
    <span data-testid={testId} className="inline-flex flex-wrap gap-1 max-w-[16rem]">
      {stages.length > 0 ? (
        stages.map(({ key, stage }) => (
          <StageBadge
            key={key}
            stage={stage.codename}
            label={stage.label}
            className="whitespace-nowrap px-1.5 text-[11px]"
          />
        ))
      ) : (
        <span className="text-gray-400 text-sm">—</span>
      )}
    </span>
  );
}

/** KAN-744: manager of the client ("-" when not assigned). */
export function ClientManagerName({ client, testId }: CellProps): JSX.Element {
  const name = client.manager?.full_name;
  return name ? (
    <span data-testid={testId} className="block truncate text-sm" title={name}>
      {name}
    </span>
  ) : (
    <span data-testid={testId} className="text-sm text-gray-400">
      -
    </span>
  );
}
